import type { OrderDetail, OrderStatus } from '@flux/shared';
import { useState } from 'react';
import { availableOrderActions, type OrderRowAction } from './order-status-actions.js';
import {
  useIgnoreOrder,
  useMarkOrderHandled,
  useReactivateOrder,
  useRefreshOrder,
  useRegenerateOrder,
  useSendOrderNow,
} from './use-order-actions.js';

export interface OrderActionTarget {
  id: string;
  name: string;
  status: OrderStatus;
  currentFileId: string | null;
  currentFileName: string | null;
}

/** Adapte un détail de commande (`02` §4) à la cible d'action commune. */
export function orderActionTargetFromDetail(order: OrderDetail): OrderActionTarget {
  return {
    id: order.id,
    name: order.name,
    status: order.status,
    currentFileId: order.currentFileId,
    currentFileName: order.currentFileName,
  };
}

export interface OrderActionController {
  order: OrderActionTarget;
  /** Actions disponibles pour cette commande, dans l'ordre du tableau `01` §4. */
  actions: OrderRowAction[];
  isBusy: boolean;
  /** Déclenche l'action : exécution immédiate, ou ouverture d'un dialogue pour celles qui en ont besoin. */
  trigger: (action: OrderRowAction) => void;
  confirmAction: 'regenerate' | null;
  closeConfirm: () => void;
  confirmPending: boolean;
  confirmTitle: string;
  confirmDescription: string;
  runConfirmed: () => void;
  ignoreOpen: boolean;
  closeIgnore: () => void;
  ignorePending: boolean;
  runIgnore: (reason: string) => void;
}

/**
 * Contrôleur partagé des actions manuelles de `01` §4 : même logique de
 * disponibilité, de confirmation (`02` §0 : conséquence rappelée) et de
 * motif obligatoire pour l'action de ligne (`order-row-actions.tsx`, menu)
 * et les boutons de l'en-tête du détail (`order-header.tsx`), qui n'ont que
 * des présentations différentes du même comportement.
 */
export function useOrderActionController(order: OrderActionTarget): OrderActionController {
  const actions = availableOrderActions(order.status);
  const [confirmAction, setConfirmAction] = useState<'regenerate' | null>(null);
  const [ignoreOpen, setIgnoreOpen] = useState(false);

  const sendNow = useSendOrderNow();
  const refresh = useRefreshOrder();
  const regenerate = useRegenerateOrder();
  const reactivate = useReactivateOrder();
  const markHandled = useMarkOrderHandled();
  const ignore = useIgnoreOrder();

  function trigger(action: OrderRowAction): void {
    switch (action) {
      case 'send-now':
        sendNow.mutate(order.id);
        return;
      case 'reactivate':
        reactivate.mutate(order.id);
        return;
      case 'mark-handled':
        markHandled.mutate(order.id);
        return;
      case 'ignore':
        setIgnoreOpen(true);
        return;
      case 'refresh':
        refresh.mutate(order.id);
        return;
      case 'regenerate':
        // `01` §4 : confirmation seulement « si un fichier a déjà été envoyé ».
        if (order.currentFileId) {
          setConfirmAction('regenerate');
        } else {
          regenerate.mutate(order.id);
        }
    }
  }

  const confirmPending = regenerate.isPending;
  const confirmTitle = 'Renvoyer la commande à Martelet';
  const confirmDescription = `La commande sera relue dans Shopify, puis un nouveau fichier sera généré et envoyé à Martelet. Si le fichier « ${order.currentFileName ?? 'courant'} » a déjà été intégré par Martelet, la commande peut y être en double. L’ancien fichier reste consultable.`;

  return {
    order,
    actions,
    isBusy:
      sendNow.isPending ||
      refresh.isPending ||
      regenerate.isPending ||
      reactivate.isPending ||
      markHandled.isPending ||
      ignore.isPending,
    trigger,
    confirmAction,
    closeConfirm: () => {
      setConfirmAction(null);
    },
    confirmPending,
    confirmTitle,
    confirmDescription,
    runConfirmed: () => {
      if (confirmAction === 'regenerate') {
        regenerate.mutate(order.id, { onSuccess: () => setConfirmAction(null) });
      }
    },
    ignoreOpen,
    closeIgnore: () => {
      setIgnoreOpen(false);
    },
    ignorePending: ignore.isPending,
    runIgnore: (reason: string) => {
      ignore.mutate({ id: order.id, reason }, { onSuccess: () => setIgnoreOpen(false) });
    },
  };
}
